// reportPopupPosition.ts
//
// Where a field-editing popup goes, and how big each kind is.
//
// The single positioner for every field popup on every report surface: the express
// editor, the reports page (`/patientReports/:_id`) and the template preview.
//
// Each surface used to place its own popups. Two of them positioned absolutely from an
// offset inside the editor's content and clamped to nothing, so once the report was
// scrolled the menu appeared far above the word it belonged to. Viewport coordinates
// with an explicit boundary is the only version that survives scrolling, so it lives
// here and there are no other copies.

export const CHAIN_POPUP_WIDTH = 260;
export const CHAIN_POPUP_HEIGHT = 150;
export const NUMERIC_POPUP_WIDTH = 220;
export const NUMERIC_POPUP_HEIGHT = 130;
export const SUGGESTION_POPUP_WIDTH = 280;
export const SUGGESTION_POPUP_HEIGHT = 200;

const REPORT_POPUP_INSET = 12;
const REPORT_POPUP_GAP = 8;

export type ReportPopupPosition = { top: number; left: number };

/**
 * Position an editor in viewport coordinates, just below its clicked field.
 * Keeping the overlay out of the report's scroll-coordinate system prevents
 * it from shifting or visually covering the field that is being edited.
 */
export const getReportPopupPosition = (
  target: HTMLElement,
  report: HTMLElement | null,
  popupWidth: number,
  popupHeight: number
): ReportPopupPosition => {
  const targetRect = target.getBoundingClientRect();
  const reportRect = report?.getBoundingClientRect();
  const boundaryLeft = Math.max(reportRect?.left ?? 0, 0);
  const boundaryRight = Math.min(reportRect?.right ?? window.innerWidth, window.innerWidth);
  const boundaryTop = Math.max(reportRect?.top ?? 0, 0);
  const boundaryBottom = Math.min(reportRect?.bottom ?? window.innerHeight, window.innerHeight);

  const minLeft = boundaryLeft + REPORT_POPUP_INSET;
  const maxLeft = Math.max(minLeft, boundaryRight - popupWidth - REPORT_POPUP_INSET);
  const desiredLeft = targetRect.right + REPORT_POPUP_GAP;

  const minTop = boundaryTop + REPORT_POPUP_INSET;
  const maxTop = Math.max(minTop, boundaryBottom - popupHeight - REPORT_POPUP_INSET);
  const belowTarget = targetRect.bottom + REPORT_POPUP_GAP;
  const aboveTarget = targetRect.top - popupHeight - REPORT_POPUP_GAP;

  return {
    left: Math.max(minLeft, Math.min(desiredLeft, maxLeft)),
    top: Math.max(minTop, Math.min(belowTarget <= maxTop ? belowTarget : aboveTarget, maxTop)),
  };
};
